import { CONSENT_EVENT } from '@/components/PostHogProvider';
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, AlertTriangle, Settings } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const STORAGE_KEY = 'meistergruen_cookie_consent';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);
  const [showSummaryView, setShowSummaryView] = useState(false);
  const [showSettingsView, setShowSettingsView] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const initialAnalyticsRef = useRef(false);
  const initialMarketingRef = useRef(false);
  const openedWithConsentRef = useRef(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setVisible(true);
    } else {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.type === 'custom') {
          setAnalytics(!!parsed.analytics);
          setMarketing(!!parsed.marketing);
        }
      } catch (_) {
        // 'accepted' / 'rejected' als String
      }
    }
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => setEntered(true), 50);
    return () => clearTimeout(t);
  }, [visible]);

  const saveAndClose = (value) => {
    localStorage.setItem(STORAGE_KEY, typeof value === 'string' ? value : JSON.stringify(value));
    // Lets analytics start (or stay off) without a reload — see
    // components/PostHogProvider.jsx, which listens for this.
    window.dispatchEvent(new Event(CONSENT_EVENT));
    setVisible(false);
  };

  const handleAcceptAll = () => {
    saveAndClose({ type: 'custom', necessary: true, analytics: true, marketing: true });
  };
  const handleRejectAll = () => {
    saveAndClose({ type: 'custom', necessary: true, analytics: false, marketing: false });
  };

  const handleSaveSettings = () => {
    saveAndClose({ type: 'custom', necessary: true, analytics, marketing });
  };

  const openSettings = () => {
    initialAnalyticsRef.current = analytics;
    initialMarketingRef.current = marketing;
    setShowSettingsView(true);
  };

  const closeSettings = () => {
    setAnalytics(initialAnalyticsRef.current);
    setMarketing(initialMarketingRef.current);
    setShowSettingsView(false);
    if (openedWithConsentRef.current) {
      setShowSummaryView(true);
    }
  };

  const openCookieModal = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      openedWithConsentRef.current = false;
      setShowSummaryView(false);
      setShowSettingsView(false);
    } else {
      openedWithConsentRef.current = true;
      setShowSummaryView(true);
      setShowSettingsView(false);
    }
    setEntered(false);
    setVisible(true);
  };

  const openSettingsFromSummary = () => {
    initialAnalyticsRef.current = analytics;
    initialMarketingRef.current = marketing;
    setShowSummaryView(false);
    setShowSettingsView(true);
  };

  if (!mounted) return null;

  return (
    <>
      {/* Cookie-Icon – sol unten, immer sichtbar */}
      <button
        type="button"
        onClick={openCookieModal}
        className="fixed left-4 bottom-4 z-30 flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#1E4D2B] text-white shadow-lg hover:bg-[#2D5F3A] transition-colors focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:ring-offset-2"
        aria-label="Cookie-Einstellungen öffnen"
      >
        <Cookie className="w-7 h-7 sm:w-8 sm:h-8" />
      </button>

      {!visible ? null : (
        <>
          {/* Backdrop – leicht abgedunkelt, Fade-in */}
          <div
            className={`fixed inset-0 bg-black/30 backdrop-blur-[2px] z-40 transition-opacity duration-400 ease-out ${
              entered ? 'opacity-100' : 'opacity-0'
            }`}
            aria-hidden="true"
          />

          {/* Modal – sol unten, Einblend-Animation (von links) */}
          <div
            className={`fixed left-4 bottom-4 z-50 w-[calc(100vw-2rem)] max-w-md transition-all duration-400 ease-out ${
              entered ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
            }`}
            role="dialog"
            aria-labelledby="cookie-title"
            aria-modal="true"
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-[#D4DCC9]/50 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-2">
                <div className="flex items-center gap-2">
                  <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-[#1E4D2B]" aria-hidden />
                  <h2 id="cookie-title" className="text-lg sm:text-xl font-semibold text-[#1E4D2B]">
                    Cookie-Einstellungen
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => setVisible(false)}
                  className="p-1.5 rounded-lg text-[#1E4D2B] hover:bg-[#F5F7F2] transition-colors"
                  aria-label="Schließen"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Hinweis-Banner – nur bei Erstöffnung (ohne Consent) */}
              {!showSummaryView && !showSettingsView && (
                <div className="mx-4 sm:mx-5 mb-4 flex items-start gap-3 rounded-xl bg-[#FEF3E2] border border-[#E8D4B8] px-3 py-2.5">
                  <AlertTriangle className="w-5 h-5 shrink-0 text-[#B8860B]" aria-hidden />
                  <p className="text-sm text-[#7A5C1E]">
                    Ihre Cookie-Zustimmung ist abgelaufen oder wurde noch nicht erteilt.
                  </p>
                </div>
              )}

              {showSettingsView ? (
            <>
              {/* Einstellungen-Ansicht: Cookie-Kategorien mit Toggles */}
              <div className="px-4 sm:px-5 pb-4 space-y-4">
                <div className="flex items-start justify-between gap-3 py-3 border-b border-[#E8EDE3]">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#1E4D2B]">Notwendige Cookies</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#E8EDE3] text-[#2D5F3A]">
                        Immer aktiv
                      </span>
                    </div>
                    <p className="text-sm text-[#2D5F3A]/80 mt-1 leading-relaxed">
                      Diese Cookies sind für die Grundfunktionen der Website erforderlich.
                    </p>
                  </div>
                  <Switch checked disabled className="shrink-0 data-[state=checked]:bg-[#1E4D2B]" />
                </div>
                <div className="flex items-start justify-between gap-3 py-3 border-b border-[#E8EDE3]">
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-[#1E4D2B]">Analyse-Cookies</span>
                    <p className="text-sm text-[#2D5F3A]/80 mt-1 leading-relaxed">
                      Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.
                    </p>
                  </div>
                  <Switch
                    checked={analytics}
                    onCheckedChange={setAnalytics}
                    className="shrink-0 data-[state=checked]:bg-[#1E4D2B]"
                  />
                </div>
                <div className="flex items-start justify-between gap-3 py-3 border-b border-[#E8EDE3]">
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-[#1E4D2B]">Marketing-Cookies</span>
                    <p className="text-sm text-[#2D5F3A]/80 mt-1 leading-relaxed">
                      Diese Cookies werden verwendet, um Ihnen relevante Werbung anzuzeigen.
                    </p>
                  </div>
                  <Switch
                    checked={marketing}
                    onCheckedChange={setMarketing}
                    className="shrink-0 data-[state=checked]:bg-[#1E4D2B]"
                  />
                </div>
              </div>
              <div className="px-4 sm:px-5 pb-4 pt-1 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  className="w-full sm:flex-1 px-5 py-3.5 rounded-xl font-semibold text-sm sm:text-base text-white bg-[#1E4D2B] hover:bg-[#2D5F3A] transition-colors min-h-[44px]"
                >
                  Einstellungen speichern
                </button>
                <button
                  type="button"
                  onClick={closeSettings}
                  className="w-full sm:flex-1 px-5 py-3.5 rounded-xl font-semibold text-sm sm:text-base text-[#1E4D2B] border-2 border-[#1E4D2B] bg-white hover:bg-[#F5F7F2] transition-colors min-h-[44px]"
                >
                  Zurück
                </button>
              </div>
            </>
          ) : showSummaryView ? (
            <>
              {/* Zusammenfassung: Einstellungen ändern, Schließen */}
              <div className="px-4 sm:px-5 pb-4 space-y-3 text-sm text-[#2D5F3A]/90 leading-relaxed">
                <p>
                  Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.
                  Einige Cookies sind notwendig für den Betrieb der Website, während andere uns helfen,
                  diese Website und die Nutzererfahrung zu verbessern (Tracking-Cookies).
                </p>
                <p>
                  Sie können selbst entscheiden, ob Sie die Cookies zulassen möchten. Bitte beachten Sie,
                  dass bei einer Ablehnung unter Umständen nicht mehr alle Funktionalitäten der Website
                  zur Verfügung stehen.
                </p>
              </div>
              <div className="px-4 sm:px-5 pb-4 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={openSettingsFromSummary}
                  className="flex-1 flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-semibold text-sm sm:text-base text-white bg-[#1E4D2B] hover:bg-[#2D5F3A] transition-colors min-h-[44px]"
                >
                  <Settings className="w-4 h-4" />
                  Einstellungen ändern
                </button>
                <button
                  type="button"
                  onClick={() => setVisible(false)}
                  className="flex-1 px-5 py-3.5 rounded-xl font-semibold text-sm sm:text-base text-[#1E4D2B] border-2 border-[#1E4D2B] bg-white hover:bg-[#F5F7F2] transition-colors min-h-[44px]"
                >
                  Schließen
                </button>
              </div>
              <div className="px-4 sm:px-5 pb-4 pt-0">
                <Link to="/datenschutz" className="text-sm font-medium text-[#1E4D2B] hover:underline">
                  Weitere Informationen zur Datenschutzerklärung
                </Link>
              </div>
            </>
          ) : (
            <>
              {/* Erstöffnung: Alle akzeptieren, Alle ablehnen, Einstellungen */}
              <div className="px-4 sm:px-5 pb-4 space-y-3 text-sm text-[#2D5F3A]/90 leading-relaxed">
                <p>
                  Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.
                  Einige Cookies sind notwendig für den Betrieb der Website, während andere uns helfen,
                  diese Website und die Nutzererfahrung zu verbessern (Tracking-Cookies).
                </p>
                <p>
                  Sie können selbst entscheiden, ob Sie die Cookies zulassen möchten. Bitte beachten Sie,
                  dass bei einer Ablehnung unter Umständen nicht mehr alle Funktionalitäten der Website
                  zur Verfügung stehen.
                </p>
              </div>
              <div className="px-4 sm:px-5 pb-4 space-y-3">
                <div className="flex flex-col xs:flex-row gap-2">
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="flex-1 px-4 py-3 rounded-xl font-semibold text-white bg-[#1E4D2B] hover:bg-[#2D5F3A] transition-colors"
                  >
                    Alle akzeptieren
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectAll}
                    className="flex-1 px-4 py-3 rounded-xl font-semibold text-[#1E4D2B] border-2 border-[#1E4D2B] bg-white hover:bg-[#F5F7F2] transition-colors"
                  >
                    Alle ablehnen
                  </button>
                </div>
                <button
                  type="button"
                  onClick={openSettings}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-[#1E4D2B] border border-[#C7D2C0] bg-white hover:bg-[#F5F7F2] transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Einstellungen
                </button>
              </div>
              <div className="px-4 sm:px-5 pb-4 pt-0">
                <Link to="/datenschutz" className="text-sm font-medium text-[#1E4D2B] hover:underline">
                  Weitere Informationen zur Datenschutzerklärung
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
        </>
      ) }
    </>
  );
};

export default CookieConsent;
